import { Component, computed, DestroyRef, inject, Inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AccountService } from 'src/app/services/account/account.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { WaypointService } from 'src/app/services/core/waypoint.service';
import { SettingsService, Unit } from 'src/app/services/account/settings.service';
import { LanguageService } from 'src/app/services/account/language.service';
import { ConsoleLoggerService } from 'src/app/services/logging/console-logger.service';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import { ECarType } from 'src/app/models/ecar-type';
import { ECar, ECarSettings } from 'src/app/models/ecar';
import { DisplayMode } from 'src/app/models/display-mode';
import { VEHICLE_IMAGE_URL } from 'src/app/services/core/http.service';
import { VehicleImageFallbackDirective } from 'src/app/directives/vehicle-image-fallback.directive';
import { SliderProperty } from '../../ui-panels/vehicle-panel/vehicle-property/vehicle-property.component';
import { RangeOptionsComponent } from './range-options/range-options.component';
import { RoutingOptionsComponent } from './routing-options/routing-options.component';
import { ChargingOptionsComponent, ChargingPage } from './charging-options/charging-options.component';
import { ChargingStationsPageComponent, ChargingStationsMode } from './charging-stations-page/charging-stations-page.component';
import { ChargingOperatorsPageComponent, ChargingOperatorsMode } from './charging-operators-page/charging-operators-page.component';
import { VehicleSelectPageComponent } from './vehicle-select-page/vehicle-select-page.component';
import { EvLoaderComponent } from '../../ui-components/ev-loader/ev-loader.component';
import { WidgetService } from 'src/app/services/platform/widget.service';

type OptionsTab = 'range' | 'routing' | 'charging';
// Left-to-right order of the tab-selector buttons; drives the sliding indicator and slide direction.
const TAB_ORDER: OptionsTab[] = ['range', 'routing', 'charging'];
type ViewMode = 'main' | 'select-vehicle' | 'charging-stations' | 'charging-operators';

const evDrivingBehaviours = [
  'efficiency_focused',
  'smooth',
  'balanced',
  'dynamic',
  'performance_oriented',
] as const;

type EvDrivingBehaviour = typeof evDrivingBehaviours[number];

// Maps each behaviour to its snap point on the 0-100 userBehavior slider.
const USER_BEHAVIOUR_STEP_VALUES: Record<EvDrivingBehaviour, number> = {
  efficiency_focused: 0,
  smooth: 25,
  balanced: 50,
  dynamic: 75,
  performance_oriented: 100,
};

const USER_BEHAVIOUR_LABEL_KEYS: Record<EvDrivingBehaviour, string> = {
  efficiency_focused: 'SliderSidebar_UserBehavior_EfficiencyFocused',
  smooth: 'SliderSidebar_UserBehavior_Smooth',
  balanced: 'SliderSidebar_UserBehavior_Balanced',
  dynamic: 'SliderSidebar_UserBehavior_Dynamic',
  performance_oriented: 'SliderSidebar_UserBehavior_PerformanceOriented',
};

// The slider works with a 1-5 step value; userBehavior itself stays 0-100 for storage/backend compatibility.
function userBehaviorValueToStep(value: number): number {
  let closestIndex = 0;
  let closestDiff = Infinity;
  evDrivingBehaviours.forEach((behaviour, index) => {
    const diff = Math.abs(USER_BEHAVIOUR_STEP_VALUES[behaviour] - value);
    if (diff < closestDiff) { closestDiff = diff; closestIndex = index; }
  });
  return closestIndex + 1;
}

function userBehaviorStepToValue(step: number): number {
  return USER_BEHAVIOUR_STEP_VALUES[evDrivingBehaviours[step - 1]];
}

@Component({
  standalone: true,
  selector: 'app-vehicle-options-dialog',
  templateUrl: './vehicle-options-dialog.component.html',
  styleUrls: ['./vehicle-options-dialog.component.scss'],
  imports: [
    CommonModule, ReactiveFormsModule, EvLoaderComponent,
    RangeOptionsComponent, RoutingOptionsComponent, ChargingOptionsComponent,
    ChargingStationsPageComponent, ChargingOperatorsPageComponent, VehicleSelectPageComponent,
    VehicleImageFallbackDirective
  ]
})
export class VehicleOptionsDialogComponent implements OnInit {
  // -------- view mode / tabs --------
  public readonly viewMode = signal<ViewMode>('main');
  // Set once a sub page hands control back to the main view. Drives the main view's slide-back
  // animation so it plays on return but not when the dialog first opens.
  public readonly returnedFromSubPage = signal(false);
  public readonly optionsTab = signal<OptionsTab>('range');
  public readonly activeTabIndex = computed(() => TAB_ORDER.indexOf(this.optionsTab()));
  // Which side the freshly shown tab content slides in from. null on the dialog's first render and
  // after returning from a sub page, when the whole main view animates instead.
  public readonly tabSlideDirection = signal<'forward' | 'back' | null>(null);
  public readonly tabSlideClass = computed(() => this.tabSlideDirection() ? `page-enter-${this.tabSlideDirection()}` : '');
  public readonly chargingStationsMode = signal<ChargingStationsMode>('favorite');
  public readonly chargingOperatorsMode = signal<ChargingOperatorsMode>('prefer');

  // -------- car list / selection --------
  public readonly CarTypes = signal<any[]>([]);
  public readonly ECars = signal<ECarType[]>([]);
  public readonly selectedCar = signal<ECarType | null>(null);
  public readonly carType = signal<string>('tesla');
  public readonly carSubType = signal<string>('Model 3');
  public readonly carTypeIndex = signal<number>(0);
  public readonly multipleCarType = signal<boolean>(false);
  public readonly editedUserCar = signal<ECar | null>(null);
  private initialUserVehicleID: number | null = null;
  public isAuthorized: boolean = false;

  // Snapshot of everything the vehicle-select page can change, taken on entry so
  // Cancel can restore it without ever having touched the backend/localStorage.
  private vehicleSelectSnapshot: {
    selectedCar: ECarType | null;
    carType: string;
    carSubType: string;
    carTypeIndex: number;
    multipleCarType: boolean;
    editedUserCar: ECar | null;
    customCarName: string;
    selectedChargerTypes: boolean[];
    batteryStateOfHealth: number;
    fastChargeLimit: number;
    tripRecordingSettings: number;
    weight: number;
    tirePressure: number;
    userBehavior: number;
    speedLimiter: number;
    minBatteryAtDestination: number;
    batterySafetyLimitRange: number;
    batterySafetyLimitRoute: number;
  } | null = null;

  // -------- routing options --------
  // Owned here (rather than in RoutingOptionsComponent) so the values survive
  // tab switches and are only persisted/applied to the calculation when OK is clicked.
  public readonly routingTollRoad = signal<number>(1);
  public readonly routingFerries = signal<number>(1);
  public readonly routingHighways = signal<number>(1);
  private initialRouting: { TollRoads: boolean, Ferries: boolean, Highways: boolean } | null = null;

  // -------- charger types --------
  public chargerTypes: { name: string, icon: string }[] = [
    { name: 'CCS', icon: 'plug_ccs.svg' }, { name: 'CHADEMO', icon: 'plug_chademo.svg' },
    { name: 'SCHUKO', icon: 'plug_schuko.svg' }, { name: 'TESLA', icon: 'plug_supercharger.svg' },
    { name: 'TYPE 1', icon: 'plug_type1.svg' }, { name: 'TYPE 2', icon: 'plug_type2.svg' }
  ];
  public readonly selectedChargerTypes = signal<boolean[]>([false, false, false, false, false, false]);
  public readonly csError = signal<boolean>(false);

  // -------- charging settings --------
  public readonly batteryStateOfHealth = signal<number>(100);
  public readonly batteryStateOfHealthMin = signal<number>(50);
  public readonly batteryStateOfHealthMax = signal<number>(110);
  
  public readonly fastChargeLimit = signal<number>(90);
  public readonly fastChargeLimitMin = signal<number>(75);
  public readonly fastChargeLimitMax = signal<number>(90);

  // -------- range/sliders --------
  public batteryLevel: number = 76;

  public weight: number = 2;
  public weightMin: number = 1;
  public weightMax: number = 5;

  public tirePressure: number = 2.3;
  public tirePressureString: string = '2.3 Bar';

  public userBehavior: number = 50;
  public userBehaviorString: string = 'normal';

  public speedLimiter: number = 100;
  public speedLimiterMin: number = 80;
  public speedLimiterMax: number = 130;
  public speedLimiterString: string = '100 km/h';

  public minBatteryAtDestination: number = 10;
  public batterySafetyLimitRange: number = 50;
  public batterySafetyRangeString: string = '';
  public batterySafetyLimitRoute: number = 15;

  public readonly sliderProperties = signal<SliderProperty[]>([]);

  // -------- form / misc --------
  public customCarNameForm: FormGroup = new FormGroup({ customCarName: new FormControl() });
  public readonly tripRecordingSettings = signal<number>(1);
  public navigation: boolean = false;

  public readonly dialogContentIsLoading = signal<boolean>(true);
  public readonly carSelectorError = signal<boolean>(false);

  readonly DisplayMode = DisplayMode;
  readonly VEHICLE_IMAGE_URL = VEHICLE_IMAGE_URL;

  private readonly destroyRef = inject(DestroyRef);
  private readonly widgetService = inject(WidgetService);

  constructor(
    public dialogRef: MatDialogRef<VehicleOptionsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public accountService: AccountService,
    private utilsService: UtilsService,
    private waypointService: WaypointService,
    public settingsService: SettingsService,
    public languageService: LanguageService,
    private consoleLoggerService: ConsoleLoggerService,
    private vehicleService: VehicleService
  ) {}

  ngOnInit(): void {
    if (this.data && this.data.editedUserCarId) {
      const editedCar = { ...this.accountService.getUserProfile().getCarById(this.data.editedUserCarId) } as ECar;
      this.editedUserCar.set(editedCar);
      this.tripRecordingSettings.set(editedCar.settings.tripRecording ?? 1);
    } else if (this.accountService.getIsAuthorized() && !(this.data && this.data.firstUserCar)) {
      // No explicit car requested (e.g. opened from the vehicle panel): default to the active vehicle.
      const selectedUserCar = this.accountService.getUserProfile()?.getSelectedUserCar();
      if (selectedUserCar) {
        const editedCar: ECar = { ...selectedUserCar };
        this.editedUserCar.set(editedCar);
        this.tripRecordingSettings.set(editedCar.settings.tripRecording ?? 1);
      }
    }
    // Remember which vehicle the dialog opened on, so we can detect an in-dialog switch on save.
    this.initialUserVehicleID = this.editedUserCar()?.userVehicleID ?? null;

    this.selectedCar.set(new ECarType(0, 0, [], 0, 0, 0, '', 0, 0, '', 0, 0, '', 0, '', '', '', ''));
    this.isAuthorized = this.accountService.getIsAuthorized();
    this.navigation = this.waypointService.getSelectedDisplayMode() === DisplayMode.NAVIGATION;

    const routing = this.settingsService.getRouting();
    this.routingTollRoad.set((routing.TollRoads == true ? 1 : 0));
    this.routingFerries.set((routing.Ferries == true ? 1 : 0));
    this.routingHighways.set((routing.Highways == true ? 1 : 0));
    this.initialRouting = { TollRoads: routing.TollRoads, Ferries: routing.Ferries, Highways: routing.Highways };

    this.batteryLevel = this.vehicleService.getBattery();

    this.loadSliderSettings();

    this.customCarNameForm = new FormGroup({ customCarName: new FormControl() });

    if (!this.isAuthorized) {
      if (this.vehicleService.getBatteryStateOfHealth()) {
        this.batteryStateOfHealth.set(this.vehicleService.getBatteryStateOfHealth());
      }
      if (this.vehicleService.getFastChargeLimit()) {
        this.fastChargeLimit.set(this.vehicleService.getFastChargeLimit()!);
      }
    }

    this.vehicleService.ObservableECarsLoaded.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (!resp) return;
      this.ECars.set([...resp.ECars].sort((a, b) => a.Name.localeCompare(b.Name)));
      if (!this.ECars() || this.ECars().length === 0) return;
      this.CarTypes.set(resp.CarTypes);

      this.selectDefaultCar();

      const editedCar = this.editedUserCar();
      if (editedCar != null) {
        this.customCarNameForm.patchValue({ customCarName: editedCar.name });
        this.selectedCar.set(editedCar.ECarType);
        if (editedCar.settings.batteryStateOfHealth) this.batteryStateOfHealth.set(editedCar.settings.batteryStateOfHealth);
        if (editedCar.settings.fastChargeLimit) this.fastChargeLimit.set(editedCar.settings.fastChargeLimit);
        this.carType.set(editedCar.ECarType!.Type!);
      } else if (!this.accountService.getIsAuthorized() && this.vehicleService.getSelectedCar()) {
        this.selectedCar.set(this.vehicleService.getSelectedCar());
        this.carType.set(this.selectedCar()?.Type!);
      }

      const subtype = this.selectedCar()?.Subtype;
      if (subtype) this.carSubType.set(subtype);
      this.carTypeIndex.set(this.getCarTypeIndex(this.carType()));

      if (!this.accountService.getIsAuthorized()) {
        this.selectedChargerTypes.set([...this.vehicleService.getChargingStations()]);
      } else {
        this.setChargerTypes(this.selectedCar()?.ChargerTypes || []);
      }

      this.multipleTypeSelector();
      this.dialogContentIsLoading.set(false);
      this.buildSliderProperties();
    });
  }

  // -------- view / tab management --------
  public openVehicleSelect(): void {
    this.vehicleSelectSnapshot = {
      selectedCar: this.selectedCar() ? { ...this.selectedCar()! } : null,
      carType: this.carType(),
      carSubType: this.carSubType(),
      carTypeIndex: this.carTypeIndex(),
      multipleCarType: this.multipleCarType(),
      editedUserCar: this.editedUserCar() ? { ...this.editedUserCar()! } : null,
      customCarName: this.customCarNameForm.get('customCarName')?.value || '',
      selectedChargerTypes: [...this.selectedChargerTypes()],
      batteryStateOfHealth: this.batteryStateOfHealth(),
      fastChargeLimit: this.fastChargeLimit(),
      tripRecordingSettings: this.tripRecordingSettings(),
      weight: this.weight,
      tirePressure: this.tirePressure,
      userBehavior: this.userBehavior,
      speedLimiter: this.speedLimiter,
      minBatteryAtDestination: this.minBatteryAtDestination,
      batterySafetyLimitRange: this.batterySafetyLimitRange,
      batterySafetyLimitRoute: this.batterySafetyLimitRoute,
    };
    this.viewMode.set('select-vehicle');
  }

  // OK on the vehicle-select page: persist the picked vehicle (backend for authorized
  // users, localStorage/vehicleService for anonymous ones) and return to the main view.
  public confirmVehicleSelect(): void {
    this.vehicleSelectSnapshot = null;
    this.saveVehicleSelection(() => { this.returnToMain(); });
  }

  // Cancel on the vehicle-select page: discard any selection made while browsing and
  // restore the state from before openVehicleSelect() was called.
  public cancelVehicleSelect(): void {
    const snap = this.vehicleSelectSnapshot;
    if (snap) {
      this.selectedCar.set(snap.selectedCar);
      this.carType.set(snap.carType);
      this.carSubType.set(snap.carSubType);
      this.carTypeIndex.set(snap.carTypeIndex);
      this.multipleCarType.set(snap.multipleCarType);
      this.editedUserCar.set(snap.editedUserCar);
      this.customCarNameForm.patchValue({ customCarName: snap.customCarName });
      this.selectedChargerTypes.set([...snap.selectedChargerTypes]);
      this.batteryStateOfHealth.set(snap.batteryStateOfHealth);
      this.fastChargeLimit.set(snap.fastChargeLimit);
      this.tripRecordingSettings.set(snap.tripRecordingSettings);
      this.weight = snap.weight;
      this.tirePressure = snap.tirePressure;
      this.userBehavior = snap.userBehavior;
      this.speedLimiter = snap.speedLimiter;
      this.minBatteryAtDestination = snap.minBatteryAtDestination;
      this.batterySafetyLimitRange = snap.batterySafetyLimitRange;
      this.batterySafetyLimitRoute = snap.batterySafetyLimitRoute;
      this.recomputeStrings();
      this.buildSliderProperties();
    }
    this.vehicleSelectSnapshot = null;
    this.returnToMain();
  }

  // Sub pages return here so the main view animates back in (see returnedFromSubPage).
  private returnToMain(): void {
    this.returnedFromSubPage.set(true);
    this.tabSlideDirection.set(null); // the whole main view animates; don't double-animate the tab content
    this.viewMode.set('main');
  }

  public setOptionsTab(tab: OptionsTab): void {
    if (tab === this.optionsTab()) { return; }
    // Content enters from the side the new tab sits on, so it follows the indicator's movement.
    this.tabSlideDirection.set(TAB_ORDER.indexOf(tab) > this.activeTabIndex() ? 'forward' : 'back');
    this.optionsTab.set(tab);
  }

  // -------- charging sub-pages (favorite/disabled stations, prefer/avoid operators) --------
  public openChargingPage(event: ChargingPage): void {
    if (event.page === 'stations') {
      this.chargingStationsMode.set(event.mode);
      this.viewMode.set('charging-stations');
    } else {
      this.chargingOperatorsMode.set(event.mode);
      this.viewMode.set('charging-operators');
    }
  }

  // Returning lands on the charging tab again, since optionsTab is preserved here.
  public closeChargingPage(): void { this.viewMode.set('main'); }

  // Main / sub car-name labels mirror the vehicle panel's selectedCarNameString /
  // selectedCarMultilineString (see VehiclePanelComponent ObservableSelectedCar):
  // a custom name shows on its own; otherwise a "Tesla Model X ..." name is split
  // across two lines ("Tesla Model X" + the remainder).
  public getCarDisplayName(): string {
    if (this.editedUserCar()?.name) return this.editedUserCar()!.name;
    const carName = this.selectedCar()?.Name || '';
    if (carName.toLocaleLowerCase().includes('tesla model')) {
      return carName.slice(0, 13);
    }
    return carName;
  }

  public getCarSubLabel(): string {
    if (this.editedUserCar()?.name) return '';
    const carName = this.selectedCar()?.Name || '';
    if (carName.toLocaleLowerCase().includes('tesla model')) {
      return carName.slice(14);
    }
    return '';
  }

  // -------- vehicle switching from select page --------
  public switchToVehicle(userVehicleID: number | null): void {
    if (userVehicleID === null) {
      // add new
      this.editedUserCar.set(null);
      this.customCarNameForm.patchValue({ customCarName: '' });
      this.selectDefaultCar();
      this.carType.set(this.selectedCar()?.Type || this.carType());
      this.carSubType.set(this.selectedCar()?.Subtype || this.carSubType());
      this.carTypeIndex.set(this.getCarTypeIndex(this.carType()));
      this.setChargerTypes(this.selectedCar()?.ChargerTypes || []);
      this.multipleTypeSelector();
      return;
    }
    const car = this.accountService.getUserProfile().getCarById(userVehicleID);
    if (!car) return;
    const editedCar: ECar = { ...car };
    this.editedUserCar.set(editedCar);
    this.tripRecordingSettings.set(editedCar.settings.tripRecording != undefined ? editedCar.settings.tripRecording : 1);
    this.customCarNameForm.patchValue({ customCarName: editedCar.name });
    this.selectedCar.set(editedCar.ECarType);
    if (editedCar.settings.batteryStateOfHealth) this.batteryStateOfHealth.set(editedCar.settings.batteryStateOfHealth);
    if (editedCar.settings.fastChargeLimit) this.fastChargeLimit.set(editedCar.settings.fastChargeLimit);
    this.carType.set(editedCar.ECarType!.Type!);
    if (editedCar.ECarType!.Subtype) this.carSubType.set(editedCar.ECarType!.Subtype);
    this.carTypeIndex.set(this.getCarTypeIndex(this.carType()));
    this.setChargerTypes(editedCar.ECarType!.ChargerTypes || []);
    this.loadSliderSettings();
    this.multipleTypeSelector();
  }

  public onVehicleDeleted(_: number): void {
    this.dialogRef.close();
  }

  // -------- slider settings --------
  private loadSliderSettings(): void {
    const settings: ECarSettings = this.editedUserCar()?.settings || this.vehicleService.getECarSettings()!;
    if (settings) {
      if (settings.weight != null) this.weight = settings.weight;
      if (settings.tirePressure != null) this.tirePressure = settings.tirePressure;
      if (settings.userBehavior != null) this.userBehavior = settings.userBehavior;
      if (settings.speedLimiter != null) this.speedLimiter = settings.speedLimiter;
      if (settings.minBatteryAtDestination != null) this.minBatteryAtDestination = settings.minBatteryAtDestination;
      if (settings.batterySafetyLimitRha != null) this.batterySafetyLimitRange = settings.batterySafetyLimitRha;
      if (settings.batterySafetyLimitRoute != null) this.batterySafetyLimitRoute = settings.batterySafetyLimitRoute;
      if (settings.tripRecording != null) this.tripRecordingSettings.set(settings.tripRecording);
    }
    // Widget mode: the service already holds the URL-applied values, so the stored
    // slider values must not override them here.
    if (!this.isAuthorized && this.settingsService.acceptCookies && !this.widgetService.isWidget()) {
      const ls = (k: string) => localStorage.getItem(k);
      if (ls('batterySafetyLimitRha')) this.batterySafetyLimitRange = parseFloat(ls('batterySafetyLimitRha')!);
      if (ls('batterySafetyLimitRoute')) this.batterySafetyLimitRoute = parseFloat(ls('batterySafetyLimitRoute')!);
      if (ls('minBatteryAtDestination')) this.minBatteryAtDestination = parseFloat(ls('minBatteryAtDestination')!);
      if (ls('speedLimiter')) this.speedLimiter = parseFloat(ls('speedLimiter')!);
      if (ls('tirePressure')) this.tirePressure = parseFloat(ls('tirePressure')!);
      if (ls('userBehavior')) this.userBehavior = parseFloat(ls('userBehavior')!);
      if (ls('weight')) this.weight = parseFloat(ls('weight')!);
      if (ls('tripRecording')) this.tripRecordingSettings.set(parseFloat(ls('tripRecording')!));
    }
    if (this.speedLimiter <= 45) this.speedLimiter = 100;

    this.recomputeStrings();
    this.buildSliderProperties();
  }

  private recomputeStrings(): void {
    // tire pressure
    const pre = (this.tirePressure - 2.3 >= 0) ? '+' : '-';
    if (this.settingsService.getUnit().Pressure === Unit.Metric) {
      this.tirePressureString = (this.tirePressure - 2.3 === 0)
        ? 'Std + 0.0 bar'
        : 'Std ' + pre + ' ' + Math.abs(Math.round((this.tirePressure - 2.3) * 10) / 10) + ' bar';
    } else {
      this.tirePressureString = 'Std ' + pre + ' ' +
        Math.abs(Math.round(this.utilsService.barToPsi(this.tirePressure) - this.utilsService.barToPsi(2.3)) * 10) / 10 + ' psi';
    }

    // user behavior
    const behaviour = evDrivingBehaviours[this.userBehaviorStep - 1];
    this.userBehaviorString = this.languageService.languageJSON?.[USER_BEHAVIOUR_LABEL_KEYS[behaviour]] || '';

    // speed limiter
    if (this.settingsService.getUnit().Speed === Unit.Metric) {
      this.speedLimiterString = this.speedLimiter + ' km/h';
    } else {
      this.speedLimiterString = Math.round(this.utilsService.kmphToMph(this.speedLimiter)) + ' mph';
    }

    // battery safety
    this.batterySafetyRangeString = this.batterySafetyLimitRange !== 50
      ? this.batterySafetyLimitRange + ' %'
      : (this.languageService.languageJSON?.SliderSidebar_BatteryReserved_ReturnLimit || '');
  }

  public buildSliderProperties(): void {
    const lang = this.languageService.languageJSON;
    this.sliderProperties.set([
      {
        id: 'batteryLevel', label: lang?.SliderSidebar_BatteryLevel || lang?.NavigationSetBatteryDialog_Title || 'Battery Level',
        displayValue: Math.round(this.batteryLevel) + ' %',
        min: 1, max: 100, digit: 0, step: 100,
        altString: 'battery level', value: this.batteryLevel, visible: true
      },
      {
        id: 'weight', label: lang?.SliderSidebar_Weight || '',
        displayValue: this.weight + ' ' + (lang?.SliderSidebar_Person?.toLowerCase() || ''),
        min: this.weightMin, max: this.weightMax, digit: 0, step: 5,
        altString: 'weight', value: this.weight, visible: true
      },
      {
        id: 'tirePressure', label: lang?.SliderSidebar_TirePressure || '',
        displayValue: this.tirePressureString,
        min: 1.5, max: 2.6, digit: 1, step: 5,
        altString: 'tire pressure', value: this.tirePressure, visible: !this.navigation
      },
      {
        id: 'userBehavior', label: lang?.SliderSidebar_UserBehavior || '',
        displayValue: this.userBehaviorString,
        min: 1, max: evDrivingBehaviours.length, digit: 0, step: evDrivingBehaviours.length,
        altString: 'user behavior', value: this.userBehaviorStep, visible: !this.navigation
      },
      {
        id: 'speedLimiter', label: lang?.SliderSidebar_SpeedLimiter || '',
        displayValue: this.speedLimiterString,
        min: this.speedLimiterMin, max: this.speedLimiterMax, digit: 0, step: 50,
        altString: 'speed limiter', value: this.speedLimiter,
        visible: !this.navigation && this.waypointService.getSelectedDisplayMode() === DisplayMode.RANGE
      },
      {
        id: 'minBatteryAtDestination', label: lang?.SliderSidebar_MinBatAtDest || '',
        displayValue: this.minBatteryAtDestination + ' %',
        min: 10, max: 75, digit: 0, step: 65,
        altString: 'min battery at destination', value: this.minBatteryAtDestination,
        visible: !this.navigation && this.waypointService.getSelectedDisplayMode() === DisplayMode.ROUTE
      },
      {
        id: 'batterySafetyLimitRha', label: lang?.SliderSidebar_BatteryReserved || '',
        displayValue: this.batterySafetyRangeString,
        min: 20, max: 50, digit: 0, step: 30,
        altString: 'battery at arrival', value: this.batterySafetyLimitRange,
        visible: !this.navigation && this.waypointService.getSelectedDisplayMode() === DisplayMode.RANGE
      },
      {
        id: 'batterySafetyLimitRoute', label: lang?.SliderSidebar_BatteryReserved || '',
        displayValue: this.batterySafetyLimitRoute + ' %',
        min: 10, max: 30, digit: 0, step: 30,
        altString: 'battery at arrival', value: this.batterySafetyLimitRoute,
        visible: !this.navigation && this.waypointService.getSelectedDisplayMode() === DisplayMode.ROUTE
      }
    ]);
  }

  // 1-5 step value for the slider; reads/writes through to the 0-100 userBehavior used for storage/backend.
  public get userBehaviorStep(): number {
    return userBehaviorValueToStep(this.userBehavior);
  }
  public set userBehaviorStep(step: number) {
    this.userBehavior = userBehaviorStepToValue(step);
  }

  public onSliderDrag({ id, value }: { id: string, value: number }): void {
    switch (id) {
      case 'batteryLevel': this.batteryLevel = value; break;
      case 'weight': this.weight = value; break;
      case 'tirePressure': this.tirePressure = value; break;
      case 'userBehavior': this.userBehaviorStep = value; break;
      case 'speedLimiter': this.speedLimiter = value; break;
      case 'minBatteryAtDestination': this.minBatteryAtDestination = value; break;
      case 'batterySafetyLimitRha': this.batterySafetyLimitRange = value; break;
      case 'batterySafetyLimitRoute': this.batterySafetyLimitRoute = value; break;
    }
    this.recomputeStrings();
    this.buildSliderProperties();
  }

  public onSliderChange(payload: { id: string, value: number }): void {
    this.onSliderDrag(payload);
  }

  public onFastChargeLimitChange(value: number): void { this.fastChargeLimit.set(value); }
  public onBatteryStateOfHealthChange(value: number): void { this.batteryStateOfHealth.set(value); }
  public onChargerTypesChanged(selectedChargerTypes: boolean[]): void {
    this.selectedChargerTypes.set(selectedChargerTypes);
    this.checkChargerTypes();
  }

  // -------- car type selection --------
  public carTypeChangedHandler(newType: string): void {
    this.carType.set(newType);
    this.carTypeIndex.set(this.getCarTypeIndex(this.carType()));
    if (this.CarTypes()[this.carTypeIndex()].subtypes.length > 0) {
      this.carSubType.set(this.CarTypes()[this.carTypeIndex()].subtypes[0]);
    }
    this.carSubTypeChangedHandler();
  }

  public carSubTypeChangedHandler(newSubType?: string): void {
    if (newSubType !== undefined) this.carSubType.set(newSubType);
    for (const ecar of this.ECars()) {
      if (this.carType() === ecar.Type) {
        if (!ecar.Subtype || (ecar.Subtype && this.carSubType() === ecar.Subtype)) {
          this.selectedCar.set({ ...ecar });
          this.setChargerTypes(this.selectedCar()!.ChargerTypes);
          break;
        }
      }
    }
  }

  public carChangedHandler(ecar: ECarType): void {
    this.selectedCar.set({ ...ecar });
    this.setChargerTypes(this.selectedCar()!.ChargerTypes);
  }

  private setChargerTypes(chargerTypesArr: number[]): void {
    // Built as a new array rather than flipped in place: an in-place write would leave the
    // signal holding the same reference and notify nothing.
    this.selectedChargerTypes.update(types => types.map((_, i) => chargerTypesArr.includes(i + 1)));
    this.multipleTypeSelector();
  }

  public checkChargerTypes(): void {
    this.csError.set(!this.selectedChargerTypes().some(v => v));
  }

  private multipleTypeSelector(): void {
    const matches = this.ECars().filter(it =>
      it.Subtype ? (it.Type === this.carType() && it.Subtype === this.carSubType()) : (it.Type === this.carType())
    );
    this.multipleCarType.set(matches.length > 1);
  }

  private getCarTypeIndex(carType: string): number {
    return this.CarTypes().findIndex(t => t.name === carType);
  }

  private selectDefaultCar(): void {
    const defCarId: number = this.utilsService.defaultCarIndex;
    for (const ecar of this.ECars()) {
      if (ecar.Id === defCarId) { this.selectedCar.set(ecar); break; }
    }
  }

  // -------- save / cancel --------
  private selectChargingStationArray(): number[] {
    const out: number[] = [];
    for (let i = 0; i < this.selectedChargerTypes().length; i++) {
      if (this.selectedChargerTypes()[i]) out.push(i + 1);
    }
    return out;
  }

  private applySliderSettings(settings: ECarSettings): ECarSettings {
    settings.weight = this.weight;
    settings.tirePressure = this.tirePressure;
    settings.userBehavior = this.userBehavior;
    settings.speedLimiter = this.speedLimiter;
    settings.minBatteryAtDestination = this.minBatteryAtDestination;
    settings.batterySafetyLimitRha = this.batterySafetyLimitRange;
    settings.batterySafetyLimitRoute = this.batterySafetyLimitRoute;
    settings.tripRecording = this.tripRecordingSettings();
    return settings;
  }

  private persistSliderSettingsToLocalStorage(): void {
    if (!this.settingsService.acceptCookies) return;
    localStorage.setItem('batterySafetyLimitRha', this.batterySafetyLimitRange.toString());
    localStorage.setItem('batterySafetyLimitRoute', this.batterySafetyLimitRoute.toString());
    localStorage.setItem('minBatteryAtDestination', this.minBatteryAtDestination.toString());
    localStorage.setItem('speedLimiter', this.speedLimiter.toString());
    localStorage.setItem('tirePressure', this.tirePressure.toString());
    localStorage.setItem('userBehavior', this.userBehavior.toString());
    localStorage.setItem('weight', this.weight.toString());
    localStorage.setItem('tripRecording', this.tripRecordingSettings().toString());
  }

  // Persist routing options (toll roads / ferries / highways) and let the EV
  // calculation pipeline pick up the change, but only once, on OK -- not on
  // every switch toggle inside the routing tab.
  private commitRoutingChanges(): void {
    if (!this.initialRouting) return;

    const tollRoads = this.routingTollRoad() > 0;
    const ferries = this.routingFerries() > 0;
    const highways = this.routingHighways() > 0;

    if (
      this.initialRouting.TollRoads === tollRoads &&
      this.initialRouting.Ferries === ferries &&
      this.initialRouting.Highways === highways
    ) {
      return;
    }

    if (this.settingsService.acceptCookies || this.accountService.getIsAuthorized()) {
      this.settingsService.setRounting(this.settingsService.routingJSONtoRoutingSettings({ tollroads: tollRoads, ferries, highways }));
    }

    if (this.accountService.getIsAuthorized()) {
      this.accountService.setUserSettings().subscribe();
    }
  }

  public clickOk(): void {
    this.commitRoutingChanges();

    if (this.csError()) return;

    // persist current SOC
    if (this.batteryLevel != null && this.vehicleService.getBattery() !== this.batteryLevel) {
      this.vehicleService.setBattery(this.batteryLevel);
      this.vehicleService.ObservableBatteryChanged.next(this.batteryLevel);
    }

    this.saveVehicleSelection(() => this.dialogRef.close());
  }

  // Persists the selected/edited vehicle to the backend (authorized users) or
  // localStorage/vehicleService (anonymous users). Shared by the main dialog's OK
  // button and by the vehicle-select page's own OK button, which only needs to
  // commit the vehicle without closing the whole dialog.
  private saveVehicleSelection(onSaved: () => void): void {
    const selectedChargersArr = this.selectChargingStationArray();

    if (this.accountService.getIsAuthorized()) {
      const cname: string = this.customCarNameForm.get('customCarName')?.value || '';
      this.carSelectorError.set(false);
      const settings = this.vehicleService.getECarSettings()!;
      if (this.editedUserCar() != null) {
        const userCar = this.accountService.getUserProfile().getCarById(this.editedUserCar()!.userVehicleID);
        // The user switched to a different vehicle inside the dialog -> make it the active one.
        const needsActivation = this.editedUserCar()!.userVehicleID !== this.initialUserVehicleID;
        const changed =
          userCar?.ECarType?.Id !== this.selectedCar()?.Id ||
          (userCar?.name && userCar.name !== cname) ||
          (userCar?.name === '' && cname !== '') ||
          userCar?.ECarType?.ChargerTypes.toString() !== selectedChargersArr.toString() ||
          userCar?.settings?.batteryStateOfHealth !== this.batteryStateOfHealth() ||
          userCar?.settings?.fastChargeLimit !== this.fastChargeLimit() ||
          userCar?.settings?.tripRecording !== this.tripRecordingSettings() ||
          userCar?.settings?.weight !== this.weight ||
          userCar?.settings?.tirePressure !== this.tirePressure ||
          userCar?.settings?.userBehavior !== this.userBehavior ||
          userCar?.settings?.speedLimiter !== this.speedLimiter ||
          userCar?.settings?.minBatteryAtDestination !== this.minBatteryAtDestination ||
          userCar?.settings?.batterySafetyLimitRha !== this.batterySafetyLimitRange ||
          userCar?.settings?.batterySafetyLimitRoute !== this.batterySafetyLimitRoute;
        if (changed) {
          settings.batteryStateOfHealth = this.batteryStateOfHealth();
          settings.fastChargeLimit = this.fastChargeLimit();
          settings.tripRecording = this.tripRecordingSettings();
          this.applySliderSettings(settings);
          this.vehicleService.ObservableTripRecordingChanged.next(this.tripRecordingSettings());
          this.dialogContentIsLoading.set(true);
          // If we also activate afterwards, let selectVehicle do the single list reload.
          this.accountService.editVehicle(this.editedUserCar()!.userVehicleID, this.selectedCar()?.Id!, cname,
            selectedChargersArr, JSON.stringify(settings), !needsActivation).subscribe({
              next: () => {
                // editedUserCar is a copy taken on open; mirror the saved values so the main view's
                // car name label doesn't keep showing the pre-edit name.
                this.editedUserCar.update(car => car ? {
                  ...car, name: cname, ECarType: { ...this.selectedCar()!, ChargerTypes: selectedChargersArr }
                } : car);
                this.finishAuthorizedSave(needsActivation, onSaved);
              },
              error: (error) => {
                this.dialogContentIsLoading.set(false);
                if (error?.name === 'TimeoutError') this.carSelectorError.set(true);
                else if (error?.status === 401) this.dialogRef.close();
              }
            });
        } else if (needsActivation) {
          this.dialogContentIsLoading.set(true);
          this.finishAuthorizedSave(true, onSaved);
        } else {
          onSaved();
        }
      } else {
        this.dialogContentIsLoading.set(true);
        this.accountService.addVehicle(this.selectedCar()?.Id!, cname, selectedChargersArr,
          this.batteryStateOfHealth(), this.fastChargeLimit(),
          this.applySliderSettings({} as ECarSettings)).subscribe({
            next: (resp: any) => {
              this.dialogContentIsLoading.set(false);
              // Point the dialog at the vehicle we just created so a later save
              // edits it instead of adding a duplicate.
              const newId = resp?.success?.id;
              const newCar = newId != null ? this.accountService.getUserProfile().getCarById(newId) : undefined;
              if (newCar) {
                this.editedUserCar.set({ ...newCar });
                this.initialUserVehicleID = newCar.userVehicleID;
              }
              onSaved();
            },
            error: (error) => {
              this.dialogContentIsLoading.set(false);
              if (error?.name === 'TimeoutError') this.carSelectorError.set(true);
              else if (error?.status === 401) this.dialogRef.close();
            }
          });
      }
    } else {
      if (this.settingsService.acceptCookies) {
        localStorage.setItem('chargingStations', this.selectedChargerTypes().toString());
        if (this.vehicleService.getBatteryStateOfHealth() !== this.batteryStateOfHealth()) {
          localStorage.setItem('batteryStateOfHealth', this.batteryStateOfHealth().toString());
          this.vehicleService.setBatteryStateOfHealth(this.batteryStateOfHealth());
        }
        if (this.vehicleService.getFastChargeLimit() !== this.fastChargeLimit()) {
          localStorage.setItem('fastChargeLimit', this.fastChargeLimit().toString());
          this.vehicleService.setFastChargeLimit(this.fastChargeLimit());
        }
      }
      this.persistSliderSettingsToLocalStorage();
      const updatedSettings = this.applySliderSettings(this.vehicleService.getECarSettings() || {} as ECarSettings);
      this.vehicleService.setSliderRangeInputParams(updatedSettings);
      this.vehicleService.ObservableTripRecordingChanged.next(this.tripRecordingSettings());
      this.selectedCar()!.ChargerTypes = selectedChargersArr;
      if (this.vehicleService.getChargingStations().toString() !== this.selectedChargerTypes().toString()) {
        this.vehicleService.setChargingStations(this.selectedChargerTypes());
      }
      if (JSON.stringify(this.vehicleService.getSelectedCar()) !== JSON.stringify(this.selectedCar())) {
        this.vehicleService.setSelectedCar(this.selectedCar()!, true);
      }
      onSaved();
    }
  }

  // After saving an authorized vehicle, optionally make it the active one before calling onSaved.
  private finishAuthorizedSave(activate: boolean, onSaved: () => void): void {
    if (activate && this.editedUserCar()) {
      this.accountService.selectVehicle(this.editedUserCar()!.userVehicleID).subscribe({
        next: () => {
          this.dialogContentIsLoading.set(false);
          this.initialUserVehicleID = this.editedUserCar()!.userVehicleID;
          onSaved();
        },
        error: (error) => {
          this.dialogContentIsLoading.set(false);
          if (error?.name === 'TimeoutError') this.carSelectorError.set(true);
          else this.dialogRef.close();
        }
      });
    } else {
      this.dialogContentIsLoading.set(false);
      if (this.editedUserCar()) this.initialUserVehicleID = this.editedUserCar()!.userVehicleID;
      onSaved();
    }
  }

  public clickCancel(): void {
    this.dialogRef.close();
  }
}
